import {
  Timestamp,
  collection,
  deleteField,
  doc,
  getDocs,
  limit,
  query,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  DEFAULT_VISIBILITY,
  STAGED_FORMAT,
  STAGE_PRESETS,
  conversionBlockers,
} from '../lib/stages';

// Legacy subcollections that hold results or game data. One doc in any of
// them keeps the tournament legacy. `players` and `maps` are carried over.
// Top-level `guessingResults` needs no check: it is only written after
// `anonymousAssignments` exist, which already blocks.
const LEGACY_DATA = [
  'groups',
  'groupStages',
  'knockout',
  'knockoutStages',
  'mapResults',
  'playerScores',
  'rounds',
  'participants',
  'teams',
  'teamScores',
  'communityVotes',
  'anonymousPlayers',
  'anonymousNames',
  'anonymousAssignments',
  'playerClips',
];

// Legacy-only settings on the tournament doc. The switch deletes them.
const LEGACY_FIELDS = [
  'stage1Config',
  'pointsTable',
  'bracketEmbeds',
  'iframeUrl',
  'link',
  'enableDrinkCounter',
  'maps',
  'stage1Maps',
  'stage2Maps',
];

const hasDocs = async (tournamentId, name) =>
  !(await getDocs(query(collection(db, 'tournaments', tournamentId, name), limit(1)))).empty;

const hasPoints = (player) =>
  Number(player.points) > 0 || Object.values(player.mapScores || {}).some((v) => Number(v) > 0);

// { blockers: string[], playerCount } - no blockers means it is safe to
// switch. `status` is the date-derived status (upcoming / active / completed).
export const checkLegacyConversion = async (tournamentId, status) => {
  const [flags, playersSnap] = await Promise.all([
    Promise.all(LEGACY_DATA.map((name) => hasDocs(tournamentId, name))),
    getDocs(collection(db, 'tournaments', tournamentId, 'players')),
  ]);
  const players = playersSnap.docs.map((d) => d.data());
  return {
    blockers: conversionBlockers({
      status,
      found: LEGACY_DATA.filter((_, i) => flags[i]),
      scoredPlayers: players.filter(hasPoints).length,
    }),
    playerCount: players.length,
  };
};

// Switches a legacy tournament to stages in one batch. Name, rules, banner,
// logo, dates, signup link, sponsors, prize pool, players and maps stay.
// Legacy-only settings go, every optional public section starts hidden, and
// one Leaderboard stage is created.
export const convertLegacyTournament = async (tournament) => {
  const now = Timestamp.now();
  const patch = {
    format: STAGED_FORMAT,
    legacyFormat: tournament.format || 'leaderboard',
    convertedAt: now,
    visibility: DEFAULT_VISIBILITY,
    votingOpen: false,
    communityAverageVisible: false,
    updatedAt: now,
  };
  LEGACY_FIELDS.forEach((field) => {
    if (field in tournament) patch[field] = deleteField();
  });
  const [stage] = STAGE_PRESETS[0].build();
  const batch = writeBatch(db);
  batch.update(doc(db, 'tournaments', tournament.id), patch);
  batch.set(doc(collection(db, 'tournaments', tournament.id, 'stages')), {
    ...stage,
    order: 0,
    createdAt: now,
    updatedAt: now,
  });
  await batch.commit();
};
